import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpRequest
} from '@angular/common/http';
import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationError } from '@angular/router';
import { StoreEndpointsConstant } from '@constants/store-endpoints.constant';
import { ValidateFields } from '@models/shopping.model';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { LayoutMediatorService } from '@services/view/layout-mediator-service/layout-mediator.service';
import { NavigationService } from '@services/view/navigation-service/navigation.service';
import { RetryConfig, Subscription, timer } from 'rxjs';
import { DataLoaderService } from 'tangoe-design-system';

export interface TrackConfig {
  count: number;
  delay: number;
  ignore_400_range: boolean | number[];
  context?: HttpContext;
}

export interface ErrorBody {
  service_version: string;
  code: string;
  timestamp: string;
  message: string;
  details: string;
}

const DEFAULT_TRACK_CONFIG: TrackConfig = {
  count: 2,
  delay: 1000,
  ignore_400_range: false
};

const TRACKER_CONTEXT_TOKEN = new HttpContextToken<Exclude<TrackConfig, 'context'>>(() => ({
  ...DEFAULT_TRACK_CONFIG
}));

const NAVIGATING_KEY: string = '----router-navigation';

export function requestTrackingContext(config: Partial<TrackConfig> | number): HttpContext {
  const { context, ...rest } = {
    ...DEFAULT_TRACK_CONFIG,
    ...(typeof config === 'number' ? { count: config } : config)
  };
  return (context ?? new HttpContext()).set(TRACKER_CONTEXT_TOKEN, rest);
}

@Injectable({
  providedIn: 'root'
})
export class RequestsTrackerService implements OnDestroy {
  private tracker: Map<string, Map<HttpRequest<any>, number | undefined>> = new Map();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private auth_svc: AuthenticationService,
    private data_loader_svc: DataLoaderService,
    private layout_mediator: LayoutMediatorService,
    private navigation_svc: NavigationService,
    private orchestrator_svc: OrchestratorService
  ) {
    this.subscriptions.add(
      this.navigation_svc.navigationError.subscribe((event: NavigationError) => {
        this.handleError(this.normalizedErrorCode(event.error));
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  reset(): void {
    this.data_loader_svc.unregister();
  }

  startRequestTracking(request: HttpRequest<any>): RetryConfig {
    const page_key: string = this.pageKey();
    this.data_loader_svc.requestInProgress(request, true);

    if (!this.tracker.has(page_key)) {
      this.tracker.clear();
      this.tracker.set(page_key, new Map());
    }
    this.tracker.get(page_key)!.set(request, undefined);

    return this.retryConfig(request);
  }

  endRequestTracking(request: HttpRequest<any>, response: any): void {
    const page_key: string = this.pageKey();
    this.data_loader_svc.requestInProgress(request, false);

    if (!this.tracker.has(page_key) || !this.tracker.get(page_key)?.has(request)) return;

    if (response === true) this.tracker.get(page_key)?.delete(request);
    else this.tracker.get(page_key)?.set(request, this.normalizedErrorCode(response));

    if (!this.tracker.get(page_key)?.size) {
      this.tracker.delete(page_key);
      return;
    }

    let error_code: number | undefined = undefined;

    for (const [_, code] of this.tracker.get(page_key)!) {
      if (code === undefined) {
        // still at least one request in progress
        error_code = undefined;
        break;
      }

      if (error_code === undefined || code < error_code) error_code = code;
    }

    if (error_code === undefined) return;

    this.tracker.delete(page_key);
    this.handleError(error_code, request);
  }

  private pageKey(): string {
    return this.navigation_svc.isNavigating() ? NAVIGATING_KEY : this.navigation_svc.url;
  }

  private retryConfig(request: HttpRequest<any>): RetryConfig {
    const { count, delay } = request.context.get(TRACKER_CONTEXT_TOKEN);

    return {
      count,
      delay: (error: any) => {
        if (!this.shouldRetry(request, error)) throw error;
        return timer(delay);
      }
    };
  }

  private handleError(code: number, request: HttpRequest<any> | undefined = undefined): void {
    if (!this.shouldRedirect(code, request)) return;

    switch (code) {
      case 0:
      case 502:
      case 503:
      case 504:
        this.orchestrator_svc.invalidate(true);
        this.layout_mediator.setError('service');
        this.reset();
        break;

      case 401:
        this.auth_svc.validateDirectLogin().subscribe({
          next: (res: HttpResponse<ValidateFields>) => {
            if (res.body?.valid) window.location.href = StoreEndpointsConstant.DIRECT_LOGIN;
            else this.handleError(res.status);
          },
          error: (error) => {
            this.handleError(error.status);
          }
        });
        break;

      case 403:
        this.layout_mediator.setError('forbidden');
        break;

      case 404:
        this.layout_mediator.setError('resource');
        break;

      case 999:
        this.layout_mediator.setError('backend');
        break;

      default:
        this.layout_mediator.setError(this.normalizedError(code, request) ?? 'request');
        break;
    }
  }

  private isSessionRequest(request: HttpRequest<any> | undefined): boolean {
    if (!request) return false;

    const method: string = request.method.toLowerCase();
    const url: string = request.url.toLowerCase();

    return method === 'get' && Boolean(url.match(/\/store\/api\/v\d+\/userprofile$/));
  }

  private shouldRetry(request: HttpRequest<any>, error: any): boolean {
    const status: number = this.normalizedErrorCode(error);
    return !this.isSessionRequest(request) && (status < 400 || status >= 500);
  }

  private shouldRedirect(code: number, request: HttpRequest<any> | undefined = undefined): boolean {
    let to_ignore: boolean | number[] =
      request?.context.get(TRACKER_CONTEXT_TOKEN).ignore_400_range ?? false;
    if (code < 400 || code >= 500 || [401, 403, 404].includes(code)) to_ignore = false;

    if ((Array.isArray(to_ignore) && to_ignore.includes(code)) || to_ignore === true) return false;

    const url: string = this.navigation_svc.navigatingTo() ?? this.navigation_svc.url;
    return !url.startsWith('/auth/') && !url.startsWith('/error/');
  }

  private normalizedErrorCode(error: any): number {
    let code: number = error instanceof HttpErrorResponse ? error.status : 999;
    if (code === 500 && (error.error?.message ?? '').match(/^Error in processing the request\.?$/i))
      code = 999;
    return code;
  }

  private normalizedError(code: number, request: HttpRequest<any> | undefined): string | undefined {
    if (code === 400 && request?.method.toLowerCase() === 'get' && request?.url.match(/\/\d+$/))
      return 'resource';

    if (code > 0 && this.isSessionRequest(request)) {
      this.orchestrator_svc.invalidate(true);
      return 'access';
    }

    return undefined;
  }
}

/*
POST 'http://localhost:8089/store/api/v1/orders'
curl --location --request DELETE 'http://localhost:8089/store/api/v1/orders/20'
curl --location --request PUT 'http://localhost:8089/store/api/v1/orders/418/submit'
curl --location --globoff --request PATCH 'http://localhost:8089/store/api/v1/line/{{lineId}}/primaryLine'
*/
